/* eslint-disable max-classes-per-file */
/* eslint-disable no-use-before-define */
import { fromEvent } from 'rxjs';
import { map, filter, share } from 'rxjs/operators';
import bridgeApp from '@utils/bridgeApp';
import {
    MessageHubInstance,
    Observable,
    MessageSourceKey,
    MessageSourceType,
    PipeConfig,
} from '../interface';

interface WrappedMessage<T = any> {
    key: MessageSourceKey;
    type: MessageSourceType;
    data: T;
}

export default class RootMessageHub<PK extends MessageSourceKey, CK extends MessageSourceKey>
    implements MessageHubInstance<PK, CK> {
    private source$: Observable<WrappedMessage>;

    private pushSource$: Observable<any>;

    private customSource$: Observable<any>;

    private pushSourceCache: Record<MessageSourceKey, Observable<any>>;

    private customSourceCache: Record<MessageSourceKey, Observable<any>>;

    constructor() {
        this.source$ = fromEvent<MessageEvent>(window, 'message').pipe(
            map(event => event.data),
            map<string, WrappedMessage>(json => {
                try {
                    const { type, ...data } = JSON.parse(json);
                    if (['shell:push', 'shell:custom'].includes(type)) {
                        return { ...data, type: type.slice('shell:'.length) };
                    }
                    return null;
                } catch {
                    return null;
                }
            }),
            filter(message => Boolean(message)),
            share(),
        );

        this.pushSource$ = this.source$.pipe(filter(data => data.type === 'push'));
        this.customSource$ = this.source$.pipe(filter(data => data.type === 'custom'));

        this.pushSourceCache = {};
        this.customSourceCache = {};
    }

    getPushSource<T extends any = any>(key: PK): Observable<T> {
        if (!this.pushSourceCache[key]) {
            this.pushSourceCache[key] = this.pushSource$.pipe(
                filter<WrappedMessage<T>>(message => {
                    return message.key === key;
                }),
                map<WrappedMessage<T>, T>(message => {
                    return message.data;
                }),
                share(),
            );

            bridgeApp.messageHub.registerPushSource(key);
        }

        return this.pushSourceCache[key];
    }

    getCustomSource<T extends any = any>(key: CK): Observable<T> {
        if (!this.customSourceCache[key]) {
            this.customSourceCache[key] = this.customSource$.pipe(
                filter<WrappedMessage<T>>(message => {
                    return message.key === key;
                }),
                map<WrappedMessage<T>, T>(message => {
                    return message.data;
                }),
                share(),
            );

            bridgeApp.messageHub.registerCustomSource(key);
        }

        return this.customSourceCache[key];
    }

    pipe(
        config?: Partial<{
            push: PipeConfig<PK>;
            custom: PipeConfig<CK>;
        }>,
    ): MessageHubInstance<PK, CK> {
        return new MessageHub(this, this.pushSourceCache, this.customSourceCache, config);
    }
}

class MessageHub<PK extends MessageSourceKey, CK extends MessageSourceKey>
    implements MessageHubInstance<PK, CK> {
    private root: RootMessageHub<PK, CK>;

    private pushSources: Record<PK, Observable<any>>;

    private customSources: Record<CK, Observable<any>>;

    constructor(
        root: RootMessageHub<PK, CK>,
        pushSources: Record<PK, Observable<any>>,
        customSources: Record<CK, Observable<any>>,
        pipeConfig?: Partial<{
            push: PipeConfig<PK>;
            custom: PipeConfig<CK>;
        }>,
    ) {
        this.root = root;

        this.pushSources = {} as Record<PK, Observable<any>>;
        const pushPipeConfig = (pipeConfig?.push || {}) as PipeConfig<PK>;
        ([
            ...new Set([...Object.keys(pushSources), ...Object.keys(pushPipeConfig)]),
        ] as PK[]).forEach(key => {
            const pushSource$ = pushSources[key];
            const pipeFunction = pushPipeConfig[key];
            if (pipeFunction) {
                this.pushSources[key] = pipeFunction(
                    // 如果传入的 Push 消息源中没有相应的 key，则通过根管理器初始化一个
                    pushSource$ || root.getPushSource(key),
                ).pipe(share());
            } else if (pushSource$) {
                this.pushSources[key] = pushSource$;
            }
        });

        this.customSources = {} as Record<CK, Observable<any>>;
        const customPipeConfig = (pipeConfig?.custom || {}) as PipeConfig<CK>;
        ([
            ...new Set([...Object.keys(customSources), ...Object.keys(customPipeConfig)]),
        ] as CK[]).forEach(key => {
            const customSource$ = customSources[key];
            const pipeFunction = customPipeConfig[key];
            if (pipeFunction) {
                this.customSources[key] = pipeFunction(
                    // 如果传入的 Custom 消息源中没有相应的 key，则通过根管理器初始化一个
                    customSource$ || root.getCustomSource(key),
                ).pipe(share());
            } else if (customSource$) {
                this.customSources[key] = customSource$;
            }
        });
    }

    getPushSource<T extends any = any>(key: PK): Observable<T> {
        if (!this.pushSources[key]) {
            this.pushSources[key] = this.root.getPushSource(key);
        }
        return this.pushSources[key];
    }

    getCustomSource<T extends any = any>(key: CK): Observable<T> {
        if (!this.customSources[key]) {
            this.customSources[key] = this.root.getCustomSource(key);
        }
        return this.customSources[key];
    }

    pipe(
        config?: Partial<{
            push: PipeConfig<PK>;
            custom: PipeConfig<CK>;
        }>,
    ): MessageHubInstance<PK, CK> {
        return new MessageHub(this.root, this.pushSources, this.customSources, config);
    }
}
